import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';

import { Amount } from '@/components/common/Amount';
import { EditableCell } from '@/components/table/EditableCell';
import { Pill, type PillTone } from '@/components/common/Pill';
import { EmptyState, TableSkeleton } from '@/components/common/Spinner';
import {
  SvgAlert,
  SvgCheckCircle,
  SvgCheveronDown,
  SvgFlag,
  SvgSquare,
  SvgTransfer,
} from '@/components/icons';
import type { TransactionEntity } from '@/api/types';
import { useAppStore } from '@/store/app';
import { useTransactionsStore } from '@/store/transactions';
import { theme } from '@/style/theme';
import { currencyToAmount, DEFAULT_CURRENCY } from '@/utils/format';
import { useDateFormat } from '@/hooks/useFormat';
import { tnum } from '@/utils/styles';

const STATUS: Record<string, { tone: PillTone; label: string; icon: ReactNode }> =
  {
    upcoming: {
      tone: 'upcoming',
      label: '即将发生',
      icon: <SvgCheveronDown width={11} height={11} />,
    },
    due: { tone: 'due', label: '到期', icon: <SvgAlert width={11} height={11} /> },
    missed: { tone: 'missed', label: '已逾期', icon: <SvgFlag width={11} height={11} /> },
    paid: { tone: 'paid', label: '已支付', icon: <SvgCheckCircle width={11} height={11} /> },
  };

function useCategoryIndex() {
  const groups = useAppStore(state => state.categoryGroups);
  return useMemo(() => {
    const nameToId = new Map<string, string>();
    const idToName = new Map<string, string>();
    for (const group of groups) {
      for (const category of group.categories) {
        nameToId.set(category.name.toLowerCase(), category.id);
        idToName.set(category.id, category.name);
      }
    }
    return { nameToId, idToName, groups };
  }, [groups]);
}

function useAccountNames() {
  const accounts = useAppStore(state => state.accounts);
  return useMemo(
    () => new Map(accounts.map(account => [account.id, account.name])),
    [accounts],
  );
}

type RowProps = {
  columns: string;
  txn: TransactionEntity;
  showAccountColumn: boolean;
  categoryName: (id: string | null) => string;
  accountName: (id: string) => string;
  onEditCategory: (id: string, name: string) => void;
  onEditPayee: (id: string, name: string) => void;
  onEditNotes: (id: string, notes: string) => void;
  onEditAmount: (id: string, amount: number, direction: 'payment' | 'deposit') => void;
  onToggleCleared: (txn: TransactionEntity) => void;
  selected: boolean;
  onToggleSelect: (id: string) => void;
};

function TransactionRow(props: RowProps) {
  const {
    columns,
    txn,
    showAccountColumn,
    categoryName,
    accountName,
    selected,
  } = props;
  const statusKey = txn.schedule_status;
  const status = statusKey ? STATUS[statusKey] : undefined;
  const isTransfer = Boolean(txn.transfer_id);
  const formatTransactionDate = useDateFormat();

  return (
    <div
      className={clsx(
        'grid items-center border-b border-table-border text-sm',
        'bg-table-background hover:bg-table-row-background-hover',
        selected && 'bg-table-row-background-highlight',
      )}
      style={{ gridTemplateColumns: columns }}
      data-testid={`transaction-row-${txn.id}`}
    >
      <div className="flex items-center justify-center">
        <button
          type="button"
          aria-label={selected ? '取消选择该交易' : '选择该交易'}
          onClick={() => props.onToggleSelect(txn.id)}
          className={clsx(
            'rounded-sm p-0.5',
            selected
              ? 'text-table-text-selected'
              : 'text-table-text-subdued hover:text-table-text',
          )}
        >
          {selected ? (
            <SvgCheckCircle width={14} height={14} />
          ) : (
            <SvgSquare width={13} height={13} />
          )}
        </button>
      </div>

      <div
        className={clsx(
          'whitespace-nowrap px-2 py-1.5',
          status && 'text-table-text-light italic',
        )}
        style={tnum}
      >
        {formatTransactionDate(txn.date)}
      </div>

      {showAccountColumn && (
        <div className="truncate px-2 py-1.5 text-table-text">
          {accountName(txn.account)}
        </div>
      )}

      <div className="truncate px-1 py-1.5">
        {status ? (
          <span className="italic text-table-text-light">
            {txn.payee_name ?? '（无收款人）'}
          </span>
        ) : (
          <EditableCell<string>
            value={txn.payee_name ?? ''}
            placeholder="（无收款人）"
            data-testid={`payee-${txn.id}`}
            parse={text => text}
            render={value => (
              <span className="flex items-center gap-1">
                {isTransfer && (
                  <SvgTransfer width={11} height={11} className="shrink-0 text-table-text-light" />
                )}
                <span className="truncate">{value}</span>
              </span>
            )}
            onCommit={next => props.onEditPayee(txn.id, next)}
          />
        )}
      </div>

      <div className="min-w-0 px-1 py-1.5">
        <EditableCell<string>
          value={txn.notes ?? ''}
          placeholder=""
          data-testid={`notes-${txn.id}`}
          parse={text => text}
          render={value => (
            <span className={clsx('truncate', !value && 'opacity-0')}>{value}</span>
          )}
          onCommit={next => props.onEditNotes(txn.id, next)}
        />
      </div>

      <div className="truncate px-2 py-1.5">
        {status ? (
          <Pill tone={status.tone} icon={status.icon}>
            {status.label}
          </Pill>
        ) : txn.is_parent ? (
          <span className="italic text-table-text-light">⌄ 拆分</span>
        ) : (
          <EditableCell<string>
            value={categoryName(txn.category)}
            placeholder=""
            data-testid={`category-${txn.id}`}
            parse={text => text}
            render={value => <span>{value}</span>}
            onCommit={next => props.onEditCategory(txn.id, next)}
          />
        )}
      </div>

      <div className="px-2 py-1.5 text-right">
        {txn.amount < 0 ? (
          <EditableCell<number>
            value={-txn.amount}
            align="right"
            data-testid={`payment-${txn.id}`}
            parse={text => currencyToAmount(text, DEFAULT_CURRENCY)}
            render={value => <Amount value={value} />}
            onCommit={next => props.onEditAmount(txn.id, next, 'payment')}
          />
        ) : null}
      </div>

      <div className="px-2 py-1.5 text-right">
        {txn.amount > 0 && !txn.is_parent ? (
          <EditableCell<number>
            value={txn.amount}
            align="right"
            data-testid={`deposit-${txn.id}`}
            parse={text => currencyToAmount(text, DEFAULT_CURRENCY)}
            render={value => <Amount value={value} />}
            onCommit={next => props.onEditAmount(txn.id, next, 'deposit')}
          />
        ) : null}
      </div>

      <div className="flex items-center justify-center">
        <button
          type="button"
          aria-label={txn.cleared ? '标记为未清算' : '标记为已清算'}
          data-testid={`cleared-${txn.id}`}
          onClick={() => props.onToggleCleared(txn)}
          className="rounded-full p-0.5"
          style={{
            color: txn.cleared
              ? theme.numberPositive
              : theme.tableBorderSeparator,
          }}
        >
          {txn.cleared ? (
            <SvgCheckCircle width={15} height={15} />
          ) : (
            <SvgSquare width={13} height={13} />
          )}
        </button>
      </div>
    </div>
  );
}

type Props = {
  transactions: TransactionEntity[];
  loading: boolean;
  showAccountColumn: boolean;
  expandedSplits: boolean;
};

export function TransactionsTable({
  transactions,
  loading,
  showAccountColumn,
  expandedSplits,
}: Props) {
  const { nameToId, idToName } = useCategoryIndex();
  const accountNames = useAccountNames();
  const update = useTransactionsStore(state => state.update);
  const selected = useTransactionsStore(state => state.selected);
  const toggleSelected = useTransactionsStore(state => state.toggleSelected);

  const columns = [
    '26px',
    '116px',
    showAccountColumn ? '150px' : null,
    'minmax(140px, 1.2fr)',
    'minmax(120px, 1.4fr)',
    '150px',
    '110px',
    '110px',
    '46px',
  ]
    .filter(Boolean)
    .join(' ');

  const rows = useMemo(() => {
    const childrenByParent = new Map<string, TransactionEntity[]>();
    const top: TransactionEntity[] = [];
    for (const txn of transactions) {
      if (txn.is_child && txn.parent_id) {
        const list = childrenByParent.get(txn.parent_id) ?? [];
        list.push(txn);
        childrenByParent.set(txn.parent_id, list);
      } else {
        top.push(txn);
      }
    }
    return { top, childrenByParent };
  }, [transactions]);

  if (loading && transactions.length === 0) {
    return (
      <TableSkeleton
        rows={10}
        columns={showAccountColumn ? 8 : 7}
        label="正在加载交易"
      />
    );
  }

  const categoryName = (id: string | null) => (id ? (idToName.get(id) ?? '') : '');
  const accountName = (id: string) => accountNames.get(id) ?? id;

  function editCategory(id: string, name: string) {
    const match = nameToId.get(name.trim().toLowerCase());
    void update(id, 'category', match ?? null);
  }

  function editAmount(
    id: string,
    value: number,
    direction: 'payment' | 'deposit',
  ) {
    void update(id, 'amount', direction === 'payment' ? -value : value);
  }

  return (
    <div
      className="scrollable min-h-0 flex-1 overflow-auto bg-table-background"
      aria-busy={loading}
      data-testid="transactions-table"
    >
      <div style={{ minWidth: showAccountColumn ? 900 : 760 }}>
        <div
          className="sticky top-0 z-10 grid border-b border-table-border bg-table-header-background text-sm text-table-header-text"
          style={{ gridTemplateColumns: columns }}
        >
          <div />
          <div className="px-2 py-2">日期</div>
          {showAccountColumn && <div className="px-2 py-2">账户</div>}
          <div className="px-2 py-2">收款人</div>
          <div className="px-2 py-2">备注</div>
          <div className="px-2 py-2">分类</div>
          <div className="px-2 py-2 text-right">支出</div>
          <div className="px-2 py-2 text-right">收入</div>
          <div className="px-2 py-2 text-center">✓</div>
        </div>

        {rows.top.map(txn => (
          <div key={txn.id}>
            <TransactionRow
              columns={columns}
              txn={txn}
              showAccountColumn={showAccountColumn}
              categoryName={categoryName}
              accountName={accountName}
              selected={selected.includes(txn.id)}
              onToggleSelect={toggleSelected}
              onEditCategory={editCategory}
              onEditPayee={(id, name) => void update(id, 'payee', name)}
              onEditNotes={(id, notes) => void update(id, 'notes', notes)}
              onEditAmount={editAmount}
              onToggleCleared={item =>
                void update(item.id, 'cleared', item.cleared ? 0 : 1)
              }
            />
            {expandedSplits &&
              (rows.childrenByParent.get(txn.id) ?? []).map(child => (
                <div key={child.id} className="border-l-2 border-table-border-selected">
                  <TransactionRow
                    columns={columns}
                    txn={child}
                    showAccountColumn={showAccountColumn}
                    categoryName={categoryName}
                    accountName={accountName}
                    selected={selected.includes(child.id)}
                    onToggleSelect={toggleSelected}
                    onEditCategory={editCategory}
                    onEditPayee={(id, name) => void update(id, 'payee', name)}
                    onEditNotes={(id, notes) => void update(id, 'notes', notes)}
                    onEditAmount={editAmount}
                    onToggleCleared={item =>
                      void update(item.id, 'cleared', item.cleared ? 0 : 1)
                    }
                  />
                </div>
              ))}
          </div>
        ))}

        {rows.top.length === 0 && (
          <EmptyState
            title="这里还没有交易"
            hint="清空右上角的搜索框，或改用「筛选」里的日期范围；也可以点「新增」记第一笔。"
          />
        )}
      </div>
    </div>
  );
}
